import React, { Fragment, useState } from 'react';

import { confirm } from '../../App/AppDialog';
import {
  Box,
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  Spacer,
  Text,
  VStack,
} from '../../../core-ui';

import { ColorPalette, DEFAULT_TAG_COLOR } from './ColorPalette';
import { Tag, TagCreateInput } from '../../../types/shared/Tag';
import { PopoverAnchor } from '@chakra-ui/react';

const MAX_NAME_LENGTH = 12;
const NEW_TAG_ID = 'NEW_TAG_FIELD';
const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const NON_HEX_CHARS = /[^0-9a-f]/gi;
const SQUARE_SIZE = 4;

type Props = {
  tags: Map<string, Tag>;
  isOpen: boolean;
  onHide: () => void;
  onTagCreate: (newTag: TagCreateInput) => void;
  onTagEditSave: (tag: Tag) => void;
  onTagDelete: (tagId: string) => void;
};

type EditState = {
  name: string;
  color: string;
};

export function VendorTagDialog(props: Props) {
  const {
    tags,
    isOpen,
    onHide,
    onTagCreate,
    onTagEditSave,
    onTagDelete,
  } = props;

  const [showColorPaletteForTag, setShowColorPaletteForTag] = useState<
    string | null
  >(null);

  const [editStateByTag, setEditStateByTag] = useState(
    () => new Map<string, EditState>(),
  );

  const newTagEditState =
    editStateByTag.get(NEW_TAG_ID) ?? createNewEditState();

  const onTagEditPress = (tag: Tag) => {
    editStateByTag.set(tag.id, {
      name: tag.name,
      color: tag.color,
    });
    setEditStateByTag(new Map(editStateByTag));
  };

  const onSaveEditing = (tag: Tag) => {
    const editState = editStateByTag.get(tag.id);
    if (!tag || !editState) {
      return;
    }
    const newTag = {
      ...tag,
      name: editState.name,
      color: validateColor(editState.color) || DEFAULT_TAG_COLOR,
    };
    onTagEditSave(newTag);
    onCancelEditing(tag.id);
  };

  const onCancelEditing = (tagId: string) => {
    editStateByTag.delete(tagId);
    setEditStateByTag(new Map(editStateByTag));
  };

  const onTagDeletePress = async (tagId: string) => {
    const proceed = await confirm(
      t('Delete Tag?'),
      t('Are you sure you want to delete this tag?'),
    );
    if (proceed) {
      onTagDelete(tagId);
    }
  };

  const renderTagNameInput = (
    tagId: string,
    editState: EditState,
    autoFocus?: boolean,
  ) => {
    const value = editState.name;
    const onChange = (value: string) => {
      editStateByTag.set(tagId, { ...editState, name: value });
      setEditStateByTag(new Map(editStateByTag));
    };
    return (
      <Input
        maxW={200}
        autoFocus={autoFocus}
        placeholder={t('Tag name...')}
        onChange={(event) => onChange(event.target.value)}
        value={value}
        maxLength={MAX_NAME_LENGTH}
      />
    );
  };

  const renderTagColorInput = (tagId: string, editState: EditState) => {
    const isColorPaletteOpen = showColorPaletteForTag === tagId;
    const value = editState.color;
    const tagColor = validateColor(value) || DEFAULT_TAG_COLOR;
    const onChange = (value: string) => {
      editStateByTag.set(tagId, { ...editState, color: sanitizeColor(value) });
      setEditStateByTag(new Map(editStateByTag));
    };
    return (
      <Box>
        <Popover
          isOpen={isColorPaletteOpen}
          onClose={() => {
            // Delay the state change because the user might have clicked into
            // the text input and that will open it again. Without this, the
            // user won't ever be able to focus the text input to type in a
            // color manually.
            setTimeout(() => {
              setShowColorPaletteForTag(null);
            }, 64);
          }}
        >
          <PopoverAnchor>
            <InputGroup maxW={200}>
              <InputLeftElement pointerEvents="none">
                <Box
                  width={SQUARE_SIZE}
                  height={SQUARE_SIZE}
                  borderRadius={2}
                  backgroundColor={tagColor}
                />
              </InputLeftElement>
              <Input
                onChange={(event) => onChange(event.target.value)}
                value={value}
                maxLength={7}
                onFocus={() => {
                  isColorPaletteOpen
                    ? setShowColorPaletteForTag(null)
                    : setShowColorPaletteForTag(tagId);
                }}
              />
            </InputGroup>
          </PopoverAnchor>
          <PopoverContent w="auto" maxW="90vw">
            <PopoverArrow />
            <PopoverBody>
              <ColorPalette
                isOpen={isColorPaletteOpen}
                selectedColor={tagColor}
                onColorSelect={(color) => {
                  onChange(color);
                }}
                onDone={() => {
                  setShowColorPaletteForTag(null);
                }}
              />
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Box>
    );
  };

  const renderTagView = (tag: Tag) => {
    const { id, name, color } = tag;
    return (
      <HStack p={1}>
        <Box
          width={SQUARE_SIZE}
          height={SQUARE_SIZE}
          borderRadius={2}
          backgroundColor={color}
        />
        <Text>{name}</Text>
        <Spacer />
        <Button variant="link" onClick={() => onTagDeletePress(id)}>
          {t('Delete')}
        </Button>
        <Button variant="link" onClick={() => onTagEditPress(tag)}>
          {t('Edit')}
        </Button>
      </HStack>
    );
  };

  const renderTagEditView = (tag: Tag, editState: EditState) => {
    return (
      <HStack p={1}>
        {renderTagNameInput(tag.id, editState)}
        {renderTagColorInput(tag.id, editState)}
        <Spacer />
        <Button onClick={() => onCancelEditing(tag.id)}>{t('Cancel')}</Button>
        <Button
          colorScheme="blue"
          variant="solid"
          onClick={() => onSaveEditing(tag)}
        >
          {t('Save')}
        </Button>
      </HStack>
    );
  };

  return (
    <Modal
      size="2xl"
      scrollBehavior="inside"
      isOpen={isOpen}
      onClose={() => onHide()}
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay />
      <ModalContent minHeight="520px">
        <ModalHeader>
          <Text>{t('Manage Tags')}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={0}>
          <VStack
            align="stretch"
            position="sticky"
            top="0"
            zIndex={1}
            backgroundColor="white"
            pt={2}
          >
            <HStack>
              {renderTagNameInput(NEW_TAG_ID, newTagEditState)}
              {renderTagColorInput(NEW_TAG_ID, newTagEditState)}
              <Spacer />
              <Button
                colorScheme="blue"
                variant="solid"
                onClick={() => {
                  const editState =
                    editStateByTag.get(NEW_TAG_ID) ?? createNewEditState();
                  const tagName = editState.name || t('New Tag');
                  const tagColor =
                    validateColor(editState.color) || DEFAULT_TAG_COLOR;
                  editStateByTag.delete(NEW_TAG_ID);
                  setEditStateByTag(new Map(editStateByTag));
                  onTagCreate({
                    name: tagName,
                    color: tagColor,
                  });
                }}
              >
                {t('Create Tag')}
              </Button>
            </HStack>
            <HStack
              pb={2}
              borderBottomWidth="thin"
              borderBottomColor="gray.200"
            >
              <Text>{t('{count} Tag(s)', { count: tags.size })}</Text>
            </HStack>
          </VStack>
          <VStack align="stretch" pb={6}>
            {Array.from(tags).map(([id, tag]) => {
              const editState = editStateByTag.get(id);
              return (
                <Fragment key={id}>
                  {editState
                    ? renderTagEditView(tag, editState)
                    : renderTagView(tag)}
                </Fragment>
              );
            })}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function createNewEditState(): EditState {
  return { name: '', color: '#' };
}

function sanitizeColor(colorValue: string): string {
  return '#' + colorValue.replace(NON_HEX_CHARS, '').slice(0, 6);
}

function validateColor(input: string): string | undefined {
  if (input.match(HEX_COLOR)) {
    return input;
  }
}
